/**
 * transcribe – typed wrapper for the `transcribe` Supabase Edge Function.
 *
 * The `transcribe` function accepts a multipart form (audio blob +
 * metadata) and returns the recognised text plus optional segments. A
 * dedicated React-Query hook (`useTranscribeAudio` in
 * `lib/transcription`) wraps the dictation flow used by the unified
 * recorder; this module exposes the same contract for one-shot callers
 * (chat sheet voice button, `SessionRecordingContext`, etc.) so they
 * stop hand-rolling the form construction and error parsing.
 */

import { supabase } from '@/integrations/supabase/client';
import { parseFunctionsError } from '@/lib/supabase-helpers';

export type TranscribeContext =
  | 'chat'
  | 'session'
  | 'dictation'
  | 'unknown'
  | string;

export interface TranscribeRequest {
  audio: Blob;
  filename?: string;
  language?: string;
  context?: TranscribeContext;
  patientId?: string;
  diarization?: boolean;
}

export interface TranscribeResponse {
  ok?: boolean;
  text: string;
  segments?: Array<{
    start: number;
    end: number;
    text: string;
    speaker?: string;
  }>;
  language?: string;
  duration_sec?: number;
  provider?: string;
}

/**
 * Build the multipart payload and invoke the transcribe function.
 *
 * Mirrors the form keys expected by `supabase/functions/transcribe`,
 * matching `lib/transcription/useTranscribeAudio` so we have a single
 * source of truth for the wire format.
 */
export async function callTranscribe(
  request: TranscribeRequest,
): Promise<TranscribeResponse> {
  const fd = new FormData();
  fd.append('audio', request.audio, request.filename ?? 'recording.webm');
  fd.append('language', request.language ?? 'de');
  if (request.context) fd.append('context', request.context);
  if (request.patientId) fd.append('patientId', request.patientId);
  if (request.diarization) fd.append('diarization', 'true');

  const { data, error } = await supabase.functions.invoke<TranscribeResponse>(
    'transcribe',
    { body: fd },
  );

  if (error) {
    const message = await parseFunctionsError(error);
    throw new Error(message);
  }

  if (!data || typeof data.text !== 'string') {
    throw new Error('transcribe returned an empty response');
  }

  return data;
}
